
import React, { useState, useEffect, useRef } from 'react';
import { Save, Shield, Lock, Globe, Check, Key, Loader2, Info, User as UserIcon, Upload, Trash2, Image as ImageIcon, Mail, Server, Eye, EyeOff } from 'lucide-react';
import { User, UserRole, EmailConfig } from '../types';

interface SettingsProps {
  onUpdateUser: (user: User) => void;
}

const SettingsView: React.FC<SettingsProps> = ({ onUpdateUser }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [customLogo, setCustomLogo] = useState<string | null>(localStorage.getItem('smsa_custom_logo'));
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  
  const [settings, setSettings] = useState({
    orgName: 'SMSA-Syria',
    locale: 'English (International)',
    encryption: true,
    anonymized: true,
    proxy: false
  });

  const [emailConfig, setEmailConfig] = useState<EmailConfig>(() => {
    const saved = localStorage.getItem('smsa_email_config');
    return saved ? JSON.parse(saved) : {
      smtpHost: 'smtp.smsa-syria.org',
      smtpPort: 587,
      smtpUser: 'noreply@smsa-syria.org',
      smtpPass: '',
      senderName: 'SMSA Portal Administrator',
      senderEmail: 'noreply@smsa-syria.org',
      useTLS: true
    };
  });
  
  const [isSaved, setIsSaved] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passStatus, setPassStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const logoInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = currentUser && [UserRole.SYSTEM_ADMIN].includes(currentUser.role);
  const isAnyAdmin = currentUser && [UserRole.SYSTEM_ADMIN, UserRole.PRESIDENT].includes(currentUser.role);

  useEffect(() => {
    const savedSettings = localStorage.getItem('smsa_settings');
    if (savedSettings) setSettings(JSON.parse(savedSettings));
    
    const user = localStorage.getItem('smsa_user');
    if (user) {
      const parsedUser = JSON.parse(user);
      setCurrentUser(parsedUser);
      setDisplayName(parsedUser.name);
    }
  }, []);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        alert("Logo file is too large. Please use an image under 1MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setCustomLogo(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setCustomLogo(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const saveSettings = () => {
    // Save organizational settings
    localStorage.setItem('smsa_settings', JSON.stringify(settings));
    localStorage.setItem('smsa_email_config', JSON.stringify(emailConfig));
    
    // Save custom logo
    if (customLogo) {
      localStorage.setItem('smsa_custom_logo', customLogo);
    } else {
      localStorage.removeItem('smsa_custom_logo');
    }
    
    // Save profile settings (name change)
    if (currentUser && displayName !== currentUser.name) {
      const updatedUser = { ...currentUser, name: displayName };
      localStorage.setItem(`smsa_profile_${currentUser.email}`, displayName);
      onUpdateUser(updatedUser);
      setCurrentUser(updatedUser);
    }

    setIsSaved(true);
    setTimeout(() => {
      setIsSaved(false);
      // If logo changed, a reload is cleaner for global state
      if (customLogo !== localStorage.getItem('smsa_custom_logo')) {
        window.location.reload();
      }
    }, 1500);
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      alert("Passwords do not match");
      return;
    }
    setPassStatus('loading');
    
    setTimeout(() => {
      localStorage.setItem(`smsa_pass_${currentUser?.email}`, passwordForm.next);
      setPassStatus('success');
      setPasswordForm({ current: '', next: '', confirm: '' });
      setTimeout(() => setPassStatus('idle'), 3000);
    }, 1000);
  };

  const toggle = (key: keyof typeof settings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="p-8 max-w-4xl mx-auto pb-32">
      <header className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Portal Configuration</h1>
          <p className="text-slate-500 font-medium">Manage your identity and organization-wide preferences.</p>
        </div>
        {isSaved && (
          <div className="flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl border border-emerald-100 animate-in fade-in zoom-in font-bold text-sm">
            <Check size={18} />
            Preferences Saved
          </div>
        )}
      </header>

      <div className="space-y-8">
        {/* Branding Section */}
        {isAnyAdmin && (
          <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 flex items-center gap-3">
              <ImageIcon className="text-indigo-600" size={20} />
              <h2 className="font-bold text-slate-800 text-lg">Branding & Identity</h2>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="w-32 h-32 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                  {customLogo ? (
                    <img src={customLogo} alt="Preview" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="text-slate-300 w-12 h-12" />
                  )}
                </div>
                <div className="flex-1 space-y-4">
                  <p className="text-xs font-medium text-slate-500 leading-relaxed">
                    Upload your official organization logo. This logo will appear on the login screen, sidebar, and all official voting transcripts. Recommended format: PNG or SVG (max 1MB).
                  </p>
                  <div className="flex gap-3">
                    <input 
                      type="file" 
                      ref={logoInputRef} 
                      onChange={handleLogoUpload} 
                      accept="image/*" 
                      className="hidden" 
                    />
                    <button 
                      onClick={() => logoInputRef.current?.click()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-indigo-700 transition-all"
                    >
                      <Upload size={16} /> Choose Logo
                    </button>
                    {customLogo && (
                      <button 
                        onClick={removeLogo}
                        className="px-4 py-2 bg-rose-50 text-rose-600 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-rose-100 transition-all"
                      >
                        <Trash2 size={16} /> Reset to Default
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Email Service Configuration - Admin Only */}
        {isAdmin && (
          <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 flex items-center gap-3">
              <Mail className="text-indigo-600" size={20} />
              <h2 className="font-bold text-slate-800 text-lg">Email Dispatch Services</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SMTP Host</label>
                  <div className="relative">
                    <Server className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input 
                      type="text" 
                      value={emailConfig.smtpHost}
                      onChange={(e) => setEmailConfig({...emailConfig, smtpHost: e.target.value})}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SMTP Port</label>
                  <input 
                    type="number" 
                    value={emailConfig.smtpPort}
                    onChange={(e) => setEmailConfig({...emailConfig, smtpPort: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SMTP Username</label>
                  <input 
                    type="text" 
                    value={emailConfig.smtpUser}
                    onChange={(e) => setEmailConfig({...emailConfig, smtpUser: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SMTP Password</label>
                  <div className="relative">
                    <input 
                      type={showSmtpPass ? "text" : "password"} 
                      value={emailConfig.smtpPass}
                      onChange={(e) => setEmailConfig({...emailConfig, smtpPass: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                    />
                    <button 
                      onClick={() => setShowSmtpPass(!showSmtpPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showSmtpPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-50 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Default Sender Name</label>
                  <input 
                    type="text" 
                    value={emailConfig.senderName}
                    onChange={(e) => setEmailConfig({...emailConfig, senderName: e.target.value})}
                    placeholder="e.g. SMSA Election Committee"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sender Email Address</label>
                  <input 
                    type="email" 
                    value={emailConfig.senderEmail}
                    onChange={(e) => setEmailConfig({...emailConfig, senderEmail: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input 
                  type="checkbox" 
                  id="useTLS" 
                  checked={emailConfig.useTLS}
                  onChange={(e) => setEmailConfig({...emailConfig, useTLS: e.target.checked})}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="useTLS" className="text-xs font-bold text-slate-600">Secure connection (STARTTLS/SSL)</label>
              </div>
            </div>
          </section>
        )}

        {/* Profile Section */}
        <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <UserIcon className="text-indigo-600" size={20} />
            <h2 className="font-bold text-slate-800 text-lg">My Identity</h2>
          </div>
          <div className="p-6">
            <div className="max-w-md space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Display Name (Full Name)</label>
                <input 
                  type="text" 
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your Name"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 font-bold text-slate-700" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Organization Email</label>
                <input 
                  disabled
                  type="text" 
                  value={currentUser?.email || ''}
                  className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl font-bold text-slate-400 cursor-not-allowed" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <Key className="text-amber-500" size={20} />
            <h2 className="font-bold text-slate-800 text-lg">Identity Security</h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Password</label>
                  <input 
                    required
                    type="password" 
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">New Password</label>
                  <input 
                    required
                    type="password" 
                    value={passwordForm.next}
                    onChange={(e) => setPasswordForm({...passwordForm, next: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Confirm New Password</label>
                  <input 
                    required
                    type="password" 
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4">
                 <div className="flex items-center gap-2 text-slate-400">
                    <Info size={14} />
                    <span className="text-xs font-medium">Use at least 8 characters with symbols</span>
                 </div>
                 <button 
                  disabled={passStatus === 'loading'}
                  type="submit" 
                  className={`px-6 py-2.5 rounded-xl font-bold transition-all text-sm flex items-center gap-2 ${
                    passStatus === 'success' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-white hover:bg-black'
                  }`}
                >
                  {passStatus === 'loading' ? <Loader2 size={16} className="animate-spin" /> : passStatus === 'success' ? <Check size={16} /> : 'Update Password'}
                  {passStatus === 'success' && 'Password Changed'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <Globe className="text-indigo-600" size={20} />
            <h2 className="font-bold text-slate-800 text-lg">General Configuration</h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Organization Name</label>
                <input 
                  type="text" 
                  value={settings.orgName}
                  onChange={(e) => setSettings({...settings, orgName: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Locale</label>
                <select 
                  value={settings.locale}
                  onChange={(e) => setSettings({...settings, locale: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold text-slate-700"
                >
                  <option>English (International)</option>
                  <option>Arabic (SA)</option>
                  <option>French (FR)</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <Lock className="text-indigo-600" size={20} />
            <h2 className="font-bold text-slate-800 text-lg">Security & Privacy</h2>
          </div>
          <div className="p-6 space-y-4">
            <ToggleOption 
              title="End-to-End Encryption" 
              description="Secure all votes with RSA-4096 cryptographic signatures."
              enabled={settings.encryption}
              onToggle={() => toggle('encryption')}
            />
            <ToggleOption 
              title="Anonymized Voting" 
              description="Hide delegate names from real-time results during live sessions."
              enabled={settings.anonymized}
              onToggle={() => toggle('anonymized')}
            />
          </div>
        </section>

        <div className="flex justify-end gap-3 pb-12">
          <button 
            onClick={saveSettings}
            className="px-10 py-3 rounded-2xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2 text-sm"
          >
            <Save size={18} />
            Commit Organization Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleOption = ({ title, description, enabled, onToggle }: { title: string, description: string, enabled: boolean, onToggle: () => void }) => (
  <div className="flex items-center justify-between py-5 border-b border-slate-50 last:border-0">
    <div className="max-w-md">
      <h3 className="font-bold text-slate-800 text-sm mb-1">{title}</h3>
      <p className="text-xs text-slate-400 leading-relaxed font-medium">{description}</p>
    </div>
    <button 
      onClick={onToggle}
      className={`w-14 h-7 rounded-full transition-all relative ${enabled ? 'bg-indigo-600' : 'bg-slate-200'}`}
    >
      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${enabled ? 'left-8' : 'left-1'}`}></div>
    </button>
  </div>
);

export default SettingsView;
